"use client";

import { useEffect, useState, useMemo } from "react";
import "./LearningAnalytics.css";
import { API_URL } from "./config";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"; // used for the Study Methods mini-chart

const TAU = 0.6; // readiness threshold (τ)

function LearningAnalytics() {
  // Common
  const [assessments, setAssessments] = useState([]);
  const [assessmentId, setAssessmentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 1) Readiness per concept
  const [readinessRows, setReadinessRows] = useState([]);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState("");

  // 2) Prereq violations
  const [violations, setViolations] = useState([]);
  const [violationsLoading, setViolationsLoading] = useState(false);
  const [violationsError, setViolationsError] = useState("");

  // 3) Difficulty (avg correctness)
  const [difficultyRows, setDifficultyRows] = useState([]);
  const [difficultyLoading, setDifficultyLoading] = useState(false);
  const [difficultyError, setDifficultyError] = useState("");

  // 4) AI feedback effectiveness
  const [aiFx, setAiFx] = useState(null); // { conversion, by_concept: [{concept, conversion}] }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // 5) Preferred study methods
  const [studyMethods, setStudyMethods] = useState([]); // [{method, students}]
  const [studyLoading, setStudyLoading] = useState(false);
  const [studyError, setStudyError] = useState("");

  // 6) Tracking progression of learning
  const [progression, setProgression] = useState(null); // { mastered, improving, stalled }
  const [progressionLoading, setProgressionLoading] = useState(false);
  const [progressionError, setProgressionError] = useState("");

  // A) Readiness Gap Map (C-level)
  const [rgRows, setRgRows] = useState([]);
  const [rgLoading, setRgLoading] = useState(false);
  const [rgError, setRgError] = useState("");

  // B) Misconception Leaderboard (C,t)
  const [mlRows, setMlRows] = useState([]); // [{concept, tag, share, students}]
  const [mlLoading, setMlLoading] = useState(false);
  const [mlError, setMlError] = useState("");

  // Fetch assessments on mount
  useEffect(() => {
    async function fetchAssessments() {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/assessments`);
        if (!res.ok) throw new Error("Failed to load assessments");
        const data = await res.json();
        const list = data.assessments || [];
        setAssessments(list);
        if (list.length > 0) setAssessmentId(list[0].id);
      } catch (e) {
        console.error(e);
        setError("Failed to load assessments. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    fetchAssessments();
  }, []);

  // Fetch all analytics when assessment changes
  useEffect(() => {
    if (!assessmentId) return;
    fetchReadiness();
    fetchViolations();
    fetchDifficulty();
    fetchAiEffectiveness();
    fetchStudyMethods();
    fetchProgression();
    fetchReadinessGaps();
    fetchMisconceptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  // ---- Fetchers (placeholders; implement these endpoints in your backend) ----
  async function fetchReadiness() {
    try {
      setReadinessLoading(true);
      setReadinessError("");
      const res = await fetch(
        `${API_URL}/api/analytics/readiness?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("readiness not available");
      const data = await res.json();
      // expect: [{ concept, attempters, readiness }] (readiness ∈ [0,1])
      setReadinessRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setReadinessRows([]);
      setReadinessError("Readiness data not available.");
    } finally {
      setReadinessLoading(false);
    }
  }

  async function fetchViolations() {
    try {
      setViolationsLoading(true);
      setViolationsError("");
      const res = await fetch(
        `${API_URL}/api/analytics/prereq-violations?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("violations not available");
      const data = await res.json();
      // expect: [{ pre, post, count, examples: [{student_id, ts}] }]
      setViolations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setViolations([]);
      setViolationsError("Prerequisite violations not available.");
    } finally {
      setViolationsLoading(false);
    }
  }

  async function fetchDifficulty() {
    try {
      setDifficultyLoading(true);
      setDifficultyError("");
      const res = await fetch(
        `${API_URL}/api/analytics/difficulty?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("difficulty not available");
      const data = await res.json();
      // expect: [{ concept, difficulty, attempts }]
      setDifficultyRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setDifficultyRows([]);
      setDifficultyError("Difficulty data not available.");
    } finally {
      setDifficultyLoading(false);
    }
  }

  async function fetchAiEffectiveness() {
    try {
      setAiLoading(true);
      setAiError("");
      const res = await fetch(
        `${API_URL}/api/analytics/ai-feedback?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("ai-feedback not available");
      const data = await res.json();
      // expect: { conversion: number (0..1), by_concept: [{ concept, conversion }] }
      setAiFx(data || null);
    } catch (e) {
      console.warn(e);
      setAiFx(null);
      setAiError("AI feedback effectiveness not available.");
    } finally {
      setAiLoading(false);
    }
  }

  async function fetchStudyMethods() {
    try {
      setStudyLoading(true);
      setStudyError("");
      const res = await fetch(
        `${API_URL}/api/analytics/study-methods?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("study-methods not available");
      const data = await res.json();
      // expect: [{ method, students }] e.g., "Hints", "Refresher", "Warm-ups", "Videos", "Docs"
      setStudyMethods(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setStudyMethods([]);
      setStudyError("Study methods data not available.");
    } finally {
      setStudyLoading(false);
    }
  }

  async function fetchProgression() {
    try {
      setProgressionLoading(true);
      setProgressionError("");
      const res = await fetch(
        `${API_URL}/api/analytics/progression?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("progression not available");
      const data = await res.json();
      // expect: { mastered: number, improving: number, stalled: number }
      setProgression(data || null);
    } catch (e) {
      console.warn(e);
      setProgression(null);
      setProgressionError("Progression data not available.");
    } finally {
      setProgressionLoading(false);
    }
  }

  async function fetchReadinessGaps() {
    try {
      setRgLoading(true);
      setRgError("");
      const res = await fetch(
        `${API_URL}/api/analytics/readiness-gaps?assessment_id=${encodeURIComponent(
          assessmentId
        )}&tau=${TAU}`
      );
      if (!res.ok) throw new Error("readiness-gaps not available");
      const data = await res.json();
      // expect: [{ concept, attempters, readiness, gap, top_missing_prereq }]
      setRgRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setRgRows([]);
      setRgError("Readiness Gap data not available.");
    } finally {
      setRgLoading(false);
    }
  }

  async function fetchMisconceptions() {
    try {
      setMlLoading(true);
      setMlError("");
      const res = await fetch(
        `${API_URL}/api/analytics/misconceptions?assessment_id=${encodeURIComponent(
          assessmentId
        )}`
      );
      if (!res.ok) throw new Error("misconceptions not available");
      const data = await res.json();
      // expect: [{ concept, tags: [{ tag, share, students }] }]
      const flat = (Array.isArray(data) ? data : []).flatMap((c) =>
        (c.tags || []).slice(0, 3).map((t) => ({
          concept: c.concept,
          tag: t.tag,
          share: t.share,
          students: t.students,
        }))
      );
      setMlRows(flat);
    } catch (e) {
      console.warn(e);
      setMlRows([]);
      setMlError("Misconception data not available.");
    } finally {
      setMlLoading(false);
    }
  }

  // ---- small helpers ----
  const studyChartData = useMemo(
    () =>
      studyMethods.map((m) => ({
        method: m.method,
        students: m.students,
      })),
    [studyMethods]
  );

  return (
    <div className="la-container">
      <header className="dashboard-header">
        <h1>Learning Analytics</h1>
        <p>Personalized learning signals powered by your knowledge graph (no time windows).</p>
      </header>

      <div className="dashboard-content">
        {/* Assessment Selector */}
        <div className="dashboard-card no-hover">
          <label htmlFor="assessmentSelect" className="selector-label">
            Select an Assessment
          </label>
          <select
            id="assessmentSelect"
            className="assessment-select"
            value={assessmentId}
            onChange={(e) => setAssessmentId(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select Assessment --</option>
            {assessments.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </select>
          {error && <div className="error-message" style={{marginTop: 8}}>{error}</div>}
        </div>

        {/* A) Readiness per concept */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Readiness per Concept</h2>
              <p className="section-subtitle">
                % of prerequisites mastered by students who attempted each concept
              </p>
            </div>
            {readinessLoading ? (
              <div className="loading-indicator">Loading readiness…</div>
            ) : readinessError ? (
              <div className="error-card"><div className="error-message">{readinessError}</div></div>
            ) : readinessRows.length === 0 ? (
              <div className="progress-empty">No readiness data found.</div>
            ) : (
              <div className="table-scroll">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Concept</th>
                      <th>Students</th>
                      <th>Readiness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readinessRows
                      .slice()
                      .sort((a, b) => (b.readiness ?? 0) - (a.readiness ?? 0))
                      .map((r, i) => (
                        <tr key={`${r.concept}-${i}`}>
                          <td className="cell-strong">{r.concept}</td>
                          <td>{safeInt(r.attempters)}</td>
                          <td>
                            <span className={`metric-badge ${readinessClass(r.readiness)}`}>
                              {safePct(r.readiness)}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* B) Prereq violations */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Prerequisite Violations</h2>
              <p className="section-subtitle">
                Attempts on B while A is weak (τ = {Math.round(TAU * 100)}%)
              </p>
            </div>
            {violationsLoading ? (
              <div className="loading-indicator">Loading violations…</div>
            ) : violationsError ? (
              <div className="error-card"><div className="error-message">{violationsError}</div></div>
            ) : violations.length === 0 ? (
              <div className="progress-empty">No violations found.</div>
            ) : (
              <div className="table-scroll">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Prereq (A)</th>
                      <th>Attempted (B)</th>
                      <th>Violations</th>
                      <th>Recent Examples</th>
                    </tr>
                  </thead>
                  <tbody>
                    {violations
                      .slice()
                      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
                      .map((v, i) => (
                        <tr key={`${v.pre}-${v.post}-${i}`}>
                          <td className="cell-strong">{v.pre}</td>
                          <td>{v.post}</td>
                          <td>{safeInt(v.count)}</td>
                          <td className="examples-cell">
                            {(v.examples || []).slice(0, 3).map((ex, idx) => (
                              <span key={idx} className="tiny-chip">
                                {ex.student_id} • {formatTs(ex.ts)}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* C) Difficulty = avg correctness */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Concept Difficulty</h2>
              <p className="section-subtitle">Average correctness across questions attached to each concept</p>
            </div>
            {difficultyLoading ? (
              <div className="loading-indicator">Loading difficulty…</div>
            ) : difficultyError ? (
              <div className="error-card"><div className="error-message">{difficultyError}</div></div>
            ) : difficultyRows.length === 0 ? (
              <div className="progress-empty">No difficulty data found.</div>
            ) : (
              <div className="table-scroll">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Concept</th>
                      <th>Attempts</th>
                      <th>Avg Correctness</th>
                      <th>Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {difficultyRows
                      .slice()
                      .sort((a, b) => (a.difficulty ?? 0) - (b.difficulty ?? 0)) // hardest first
                      .map((d, i) => (
                        <tr key={`${d.concept}-${i}`}>
                          <td className="cell-strong">{d.concept}</td>
                          <td>{safeInt(d.attempts)}</td>
                          <td>
                            <span className={`metric-badge ${difficultyClass(d.difficulty)}`}>
                              {safePct(d.difficulty)}
                            </span>
                          </td>
                          <td>{difficultyFlag(d.difficulty)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* D) AI feedback effectiveness */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>AI Feedback Effectiveness</h2>
              <p className="section-subtitle">Did hints lead to the next attempt being correct?</p>
            </div>
            {aiLoading ? (
              <div className="loading-indicator">Loading AI effectiveness…</div>
            ) : aiError ? (
              <div className="error-card"><div className="error-message">{aiError}</div></div>
            ) : !aiFx ? (
              <div className="progress-empty">No AI feedback data found.</div>
            ) : (
              <>
                <div className="ai-summary">
                  Overall conversion:&nbsp;
                  <span className="metric-badge">{safePct(aiFx.conversion)}</span>
                </div>
                {Array.isArray(aiFx.by_concept) && aiFx.by_concept.length > 0 && (
                  <div className="table-scroll">
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Concept</th>
                          <th>Conversion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiFx.by_concept
                          .slice()
                          .sort((a, b) => (b.conversion ?? 0) - (a.conversion ?? 0))
                          .map((row, i) => (
                            <tr key={`${row.concept}-${i}`}>
                              <td className="cell-strong">{row.concept}</td>
                              <td>{safePct(row.conversion)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* E) Understanding preferred study methods */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Preferred Study Methods</h2>
              <p className="section-subtitle">What students actually use: hints, refreshers, warm-ups, videos, docs…</p>
            </div>
            {studyLoading ? (
              <div className="loading-indicator">Loading study methods…</div>
            ) : studyError ? (
              <div className="error-card"><div className="error-message">{studyError}</div></div>
            ) : studyMethods.length === 0 ? (
              <div className="progress-empty">No study-method data found.</div>
            ) : (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={studyChartData} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="method" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="students" name="Students" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* F) Tracking progression of learning */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Progression of Learning</h2>
              <p className="section-subtitle">How many are mastered, improving, or stalled.</p>
            </div>
            {progressionLoading ? (
              <div className="loading-indicator">Loading progression…</div>
            ) : progressionError ? (
              <div className="error-card"><div className="error-message">{progressionError}</div></div>
            ) : !progression ? (
              <div className="progress-empty">No progression data found.</div>
            ) : (
              <div className="progress-kpis">
                <div className="stat-item correct">
                  <span className="stat-number">{safeInt(progression.mastered)}</span>
                  <span className="stat-label">Mastered</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{safeInt(progression.improving)}</span>
                  <span className="stat-label">Improving</span>
                </div>
                <div className="stat-item semantic-error">
                  <span className="stat-number">{safeInt(progression.stalled)}</span>
                  <span className="stat-label">Stalled</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 1) Readiness Gap Map */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Readiness Gap Map</h2>
              <p className="section-subtitle">
                Concepts many students try before prerequisites are learned (τ = {Math.round(TAU * 100)}%)
              </p>
            </div>
            {rgLoading ? (
              <div className="loading-indicator">Loading readiness gaps…</div>
            ) : rgError ? (
              <div className="error-card"><div className="error-message">{rgError}</div></div>
            ) : rgRows.length === 0 ? (
              <div className="progress-empty">No gaps found.</div>
            ) : (
              <div className="table-scroll">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Concept</th>
                      <th>Students</th>
                      <th>Readiness</th>
                      <th>Gap</th>
                      <th>Top Missing Prereq</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rgRows
                      .slice()
                      .sort((a, b) => (b.gap ?? 0) - (a.gap ?? 0))
                      .map((r, i) => (
                        <tr key={`${r.concept}-${i}`}>
                          <td className="cell-strong">{r.concept}</td>
                          <td>{safeInt(r.attempters)}</td>
                          <td>
                            <span className={`metric-badge ${readinessClass(r.readiness)}`}>
                              {safePct(r.readiness)}
                            </span>
                          </td>
                          <td>
                            <span className={`metric-badge ${gapClass(r.gap)}`}>{safeNum(r.gap)}</span>
                          </td>
                          <td>{r.top_missing_prereq || "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 2) Misconception Leaderboard */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <div className="section-header">
              <h2>Misconception Leaderboard</h2>
              <p className="section-subtitle">Top recurring mistakes per concept (share of attempters)</p>
            </div>
            {mlLoading ? (
              <div className="loading-indicator">Loading misconceptions…</div>
            ) : mlError ? (
              <div className="error-card"><div className="error-message">{mlError}</div></div>
            ) : mlRows.length === 0 ? (
              <div className="progress-empty">No misconception data found.</div>
            ) : (
              <div className="table-scroll">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Concept</th>
                      <th>Mistake</th>
                      <th>Share</th>
                      <th>Students</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mlRows.map((row, i) => (
                      <tr key={`${row.concept}-${row.tag}-${i}`}>
                        <td className="cell-strong">{row.concept}</td>
                        <td><span className="tag-chip">{row.tag}</span></td>
                        <td>{safePct(row.share)}</td>
                        <td>{safeInt(row.students)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- helpers ----
function safePct(n) {
  if (typeof n !== "number") return "—";
  return `${Math.round(n * 100)}%`;
}
function safeInt(n) {
  if (typeof n !== "number") return "—";
  return n.toLocaleString();
}
function safeNum(n) {
  if (typeof n !== "number") return "—";
  return n.toFixed(2);
}
function readinessClass(p) {
  if (typeof p !== "number") return "";
  if (p >= 0.8) return "is-good";
  if (p >= 0.6) return "is-warn";
  return "is-bad";
}
function gapClass(g) {
  if (typeof g !== "number") return "";
  if (g >= 40) return "is-bad";
  if (g >= 15) return "is-warn";
  return "";
}
function difficultyClass(p) {
  if (typeof p !== "number") return "";
  if (p >= 0.9) return "is-good";
  if (p <= 0.3) return "is-bad";
  return "";
}
function difficultyFlag(p) {
  if (typeof p !== "number") return "—";
  if (p >= 0.9) return "Too easy";
  if (p <= 0.3) return "Too hard";
  return "OK";
}
function formatTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString();
  } catch {
    return String(ts);
  }
}

export default LearningAnalytics;
